import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';

const API = 'https://api.glowytics.com/api/wunder-cost-per-sale';

// --- formatlayıcılar ---
const fmtTRY = (v, d = 0) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 }));
const fmtPct = (v, d = 1) => (v == null ? '—' : `%${(Number(v) * 100).toLocaleString('tr-TR', { maximumFractionDigits: d })}`);

// platform etiketleri
const SRC_LABEL = {
  meta: 'Meta',
  'google ads': 'Google Ads',
  pinterest: 'Pinterest',
  'rtb house': 'RTB House',
  criteo: 'Criteo',
  tiktok: 'TikTok',
};
const label = (s) => SRC_LABEL[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const fmtMonth = (iso) => {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  return `${TR_MONTHS[Number(m) - 1]} ${y}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Hafta etiketi: "23 – 29 Haz" (aynı ay) / "29 Haz – 5 Tem" (ay aşıyorsa)
const fmtWeek = (startIso, endIso) => {
  if (!startIso) return '—';
  const [, sm, sd] = startIso.split('-');
  const e = endIso || addDaysISO(startIso, 6);
  const [, em, ed] = e.split('-');
  const sMon = TR_MONTHS[Number(sm) - 1];
  const eMon = TR_MONTHS[Number(em) - 1];
  return sm === em
    ? `${Number(sd)} – ${Number(ed)} ${eMon}`
    : `${Number(sd)} ${sMon} – ${Number(ed)} ${eMon}`;
};

// Maliyet metriği: düşmek İYİ (yeşil). ROAS gibi metriklerde tersine çevirmek için lowerIsBetter=false.
function Delta({ curr, prev, lowerIsBetter = true, suffix = '' }) {
  if (curr == null || prev == null || prev === 0) return <span className="text-gray-300 text-xs">—</span>;
  const ch = (curr - prev) / Math.abs(prev);
  const improved = lowerIsBetter ? curr < prev : curr > prev;
  const color = Math.abs(ch) < 0.001 ? 'text-gray-400' : improved ? 'text-green-600' : 'text-red-600';
  const arrow = ch > 0 ? '▲' : ch < 0 ? '▼' : '';
  return (
    <span className={`${color} text-xs font-semibold`}>
      {arrow} {fmtPct(Math.abs(ch))}
      {suffix}
    </span>
  );
}

function Stat({ label: l, value }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 min-w-[140px]">
      <div className="text-xs text-gray-500">{l}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

// Aylık & Haftalık kırılım için ortak tablo (ikisi de aynı kolon yapısını paylaşır).
// periodKey: 'month' | 'week'; rows backend'den gelen satırlar; labelOf(row) → başlık.
function BreakdownTable({ rows, periodKey, labelOf, periodHeader, open, toggle, defaultOpen = false }) {
  return (
    <div className="overflow-auto border border-gray-200 rounded-xl">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{periodHeader}</th>
            <th className="text-right px-4 py-2 font-medium">Harcama</th>
            <th className="text-right px-4 py-2 font-medium">Sipariş</th>
            <th className="text-right px-4 py-2 font-medium">Ciro</th>
            <th className="text-right px-4 py-2 font-medium">GA4 oranı</th>
            <th className="text-right px-4 py-2 font-medium">Sipariş başına maliyet</th>
            <th className="text-right px-4 py-2 font-medium">Ciro oranı</th>
            <th className="text-right px-4 py-2 font-medium">ROAS</th>
            <th className="text-right px-4 py-2 font-medium">Yeni müşteri başına (CAC)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pk = r[periodKey];
            const isOpen = open[pk] === undefined ? defaultOpen : open[pk] === true;
            const plats = r.platforms || [];
            return (
              <React.Fragment key={pk}>
                <tr
                  className="border-t border-gray-200 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggle(pk, isOpen)}
                >
                  <td className="px-4 py-2 font-medium text-gray-900">
                    <span className="inline-block w-4 text-gray-400">{plats.length ? (isOpen ? '▾' : '▸') : ''}</span>
                    {labelOf(r)}
                  </td>
                  <td className="px-4 py-2 text-right">{fmtTRY(r.spend)}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{fmtNum(r.orders)}</td>
                  <td className="px-4 py-2 text-right">{fmtTRY(r.revenue)}</td>
                  <td className="px-4 py-2 text-right text-gray-300">—</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtTRY(r.costPerOrder)}</td>
                  <td className="px-4 py-2 text-right">{fmtPct(r.spendRevenueRatio)}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{r.roas ? `${r.roas.toFixed(2)}×` : '—'}</td>
                  <td className="px-4 py-2 text-right">{fmtTRY(r.costPerNewCustomer)}</td>
                </tr>
                {isOpen &&
                  plats.map((p) => (
                    <tr
                      key={pk + p.source}
                      className={`text-[13px] ${p.isOther ? 'bg-gray-100 text-gray-500 italic' : 'bg-gray-50/60 text-gray-600'}`}
                    >
                      <td className="pl-10 pr-4 py-1.5">↳ {label(p.source)}</td>
                      <td className="px-4 py-1.5 text-right">{p.isOther ? '—' : fmtTRY(p.spend)}</td>
                      <td className="px-4 py-1.5 text-right">{fmtNum(Math.round(p.allocOrders))}</td>
                      <td className="px-4 py-1.5 text-right">{fmtTRY(p.allocRevenue)}</td>
                      <td
                        className="px-4 py-1.5 text-right text-indigo-600"
                        title="GA4 payı (payda: tüm-kanal GA4) — sipariş & ciro bazında"
                      >
                        <div className="font-medium">
                          {fmtPct(p.ga4OrderShare)} <span className="text-[10px] text-indigo-400">adet</span>
                        </div>
                        <div className="text-[11px] text-gray-400">{fmtPct(p.ga4RevenueShare)} ciro</div>
                      </td>
                      <td className="px-4 py-1.5 text-right" title="harcama / GA4-oranıyla atfedilen ikas sipariş">
                        {p.allocCostPerSale != null ? fmtTRY(p.allocCostPerSale) : '—'}
                      </td>
                      <td className="px-4 py-1.5" colSpan={3} />
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function WunderCostPerSale() {
  const [end, setEnd] = useState(todayISO());
  const [start, setStart] = useState(addDaysISO(todayISO(), -29));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [breakdownView, setBreakdownView] = useState('monthly'); // 'monthly' | 'weekly'
  const [openMonths, setOpenMonths] = useState({}); // ay açık/kapalı (varsayılan kapalı)
  const [openWeeks, setOpenWeeks] = useState({}); // hafta açık/kapalı (varsayılan kapalı)

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/overview?start=${start}&end=${end}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      toast.error('Rapor yüklenemedi. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Aylık kırılım — tarih seçiciden bağımsız, son 6 ay (mount'ta bir kez)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/monthly?months=18`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setMonthly(await res.json());
      } catch (err) {
        toast.error('Aylık kırılım yüklenemedi. ' + err.message);
      }
    })();
  }, []);

  // Haftalık kırılım — son 90 gün (≈13 hafta), tarih seçiciden bağımsız (mount'ta bir kez)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/weekly?days=90`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setWeekly(await res.json());
      } catch (err) {
        toast.error('Haftalık kırılım yüklenemedi. ' + err.message);
      }
    })();
  }, []);

  const cur = data?.current;
  const prev = data?.previous;

  // hızlı dönem butonları
  const setRange = (n) => {
    const e = todayISO();
    setEnd(e);
    setStart(addDaysISO(e, -(n - 1)));
  };

  // mecra toplam dönüşümü ikas gerçeğini aşıyor mu? (over-reporting uyarısı)
  const overReport = useMemo(() => {
    if (!cur) return null;
    if (!cur.orders || !cur.totalConv) return null;
    return cur.totalConv > cur.orders ? cur.totalConv / cur.orders : null;
  }, [cur]);

  return (
    <div className="p-6 max-w-[1180px]">
      <div className="mb-1">
        <h1 className="text-2xl font-bold text-gray-900">Bir Satışın Maliyeti — Wunder</h1>
        <p className="text-sm text-gray-500">
          Toplam reklam harcaması / satış (ikas <b>{data?.channel || 'wunder'}</b> kanalı). Önceki eşit döneme göre
          değişim oklarla gösterilir.
        </p>
      </div>

      {/* Dönem seçici */}
      <div className="flex flex-wrap items-end gap-3 my-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Başlangıç</label>
          <input
            type="date"
            value={start}
            max={end}
            onChange={(e) => setStart(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Bitiş</label>
          <input
            type="date"
            value={end}
            max={todayISO()}
            onChange={(e) => setEnd(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => setRange(n)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Son {n}g
            </button>
          ))}
        </div>
        {prev && (
          <span className="text-xs text-gray-400 ml-auto">
            Karşılaştırma: {prev.range.start} → {prev.range.end} ({cur?.range.days} gün)
          </span>
        )}
      </div>

      {loading && <div className="text-gray-500 py-10">Yükleniyor…</div>}
      {error && <div className="text-red-600 py-10">Hata: {error}</div>}

      {!loading && !error && cur && (
        <>
          {/* 3 AÇI */}
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="flex-1 min-w-[220px] bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="text-sm text-gray-500 mb-1">Sipariş başına maliyet</div>
              <div className="text-3xl font-bold text-gray-900">{fmtTRY(cur.costPerOrder, 0)}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">toplam harcama / sipariş</span>
                <Delta curr={cur.costPerOrder} prev={prev?.costPerOrder} lowerIsBetter />
              </div>
            </div>

            <div className="flex-1 min-w-[220px] bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="text-sm text-gray-500 mb-1">Ciro oranı (harcama / ciro)</div>
              <div className="text-3xl font-bold text-gray-900">{fmtPct(cur.spendRevenueRatio)}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">ROAS {cur.roas ? cur.roas.toFixed(2) : '—'}×</span>
                <Delta curr={cur.spendRevenueRatio} prev={prev?.spendRevenueRatio} lowerIsBetter />
              </div>
            </div>

            <div className="flex-1 min-w-[220px] bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="text-sm text-gray-500 mb-1">Yeni müşteri başına maliyet (CAC)</div>
              <div className="text-3xl font-bold text-gray-900">{fmtTRY(cur.costPerNewCustomer, 0)}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">toplam harcama / yeni müşteri</span>
                <Delta curr={cur.costPerNewCustomer} prev={prev?.costPerNewCustomer} lowerIsBetter />
              </div>
            </div>
          </div>

          {/* Özet satır */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Stat label="Toplam harcama" value={fmtTRY(cur.totalSpend)} />
            <Stat label="Sipariş" value={fmtNum(cur.orders)} />
            <Stat label="Ürün adedi" value={fmtNum(cur.items)} />
            <Stat label="Ciro" value={fmtTRY(cur.revenue)} />
            <Stat label="Yeni müşteri" value={fmtNum(cur.newCustomers)} />
          </div>

          {/* Mecra kırılımı */}
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Mecra Bazında (her mecra kendi içinde)</h2>
          </div>
          {overReport && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
              ⚠️ Mecraların kendi raporladığı toplam dönüşüm ({fmtNum(cur.totalConv)}), ikas gerçek siparişinin (
              {fmtNum(cur.orders)}) <b>{overReport.toFixed(1)}×</b> katı. Platformlar aynı satışı üst üste sayabildiği
              için mecra-bazı CPA değerleri ikas gerçeğinden iyimser olabilir; gerçek maliyet için yukarıdaki "Sipariş
              başına maliyet" geçerlidir.
            </div>
          )}
          <div className="overflow-auto border border-gray-200 rounded-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Mecra</th>
                  <th className="text-right px-4 py-2 font-medium">Harcama</th>
                  <th className="text-right px-4 py-2 font-medium">Harcama payı</th>
                  <th className="text-right px-4 py-2 font-medium">Dönüşüm (kendi)</th>
                  <th className="text-right px-4 py-2 font-medium">Dönüşüm payı</th>
                  <th className="text-right px-4 py-2 font-medium">Satış başına maliyet (CPA)</th>
                </tr>
              </thead>
              <tbody>
                {cur.platforms.map((p) => {
                  const pp = prev?.platforms.find((x) => x.source === p.source);
                  return (
                    <tr key={p.source} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-medium text-gray-900">{label(p.source)}</td>
                      <td className="px-4 py-2 text-right">{fmtTRY(p.spend)}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{fmtPct(p.spendShare)}</td>
                      <td className="px-4 py-2 text-right">{fmtNum(p.conv)}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{fmtPct(p.convShare)}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-semibold">{fmtTRY(p.costPerConv)}</span>
                          <Delta curr={p.costPerConv} prev={pp?.costPerConv} lowerIsBetter />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {cur.platforms.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                      Bu dönemde harcama bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold text-gray-900">
                <tr className="border-t border-gray-200">
                  <td className="px-4 py-2">Toplam</td>
                  <td className="px-4 py-2 text-right">{fmtTRY(cur.totalSpend)}</td>
                  <td className="px-4 py-2 text-right text-gray-400">%100</td>
                  <td className="px-4 py-2 text-right">{fmtNum(cur.totalConv)}</td>
                  <td className="px-4 py-2 text-right text-gray-400">%100</td>
                  <td className="px-4 py-2 text-right">
                    {fmtTRY(cur.totalConv > 0 ? cur.totalSpend / cur.totalConv : null)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Kırılım — Aylık / Haftalık toggle (tarih seçiciden bağımsız) */}
      {(monthly?.rows?.length > 0 || weekly?.rows?.length > 0) && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h2 className="text-lg font-semibold text-gray-900">
              {breakdownView === 'monthly'
                ? `Aylık Kırılım — Geçen Yıl + Bu Yıl (${monthly?.rows?.length || 0} ay)`
                : `Haftalık Kırılım — Son 90 Gün (${weekly?.rows?.length || 0} hafta)`}
            </h2>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {[
                { key: 'monthly', lbl: 'Aylık' },
                { key: 'weekly', lbl: 'Haftalık' },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setBreakdownView(t.key)}
                  className={`px-4 py-1.5 ${
                    breakdownView === t.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t.lbl}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            {breakdownView === 'monthly' ? 'Ay' : 'Hafta'} satırı: toplam harcama / <b>ikas {(breakdownView === 'monthly' ? monthly : weekly)?.channel}</b> kanalı satışı
            (gerçek). Satıra tıklayınca mecra kırılımı açılır: <b>GA4 oranı</b> kolonu her mecranın tüm-kanal GA4 içindeki
            sipariş & ciro payını gösterir; bu oran <b>ikas gerçeğine</b> uygulanır → "Sipariş" ve "Ciro" kolonlarındaki
            adet/₺ o oranla ikas'tan atfedilendir. Mecra + <b>Diğer</b> (organik/direkt vb.) toplamı = dönemin ikas
            gerçeği. Satış başına maliyet = harcama / atfedilen ikas sipariş. İçinde bulunulan {breakdownView === 'monthly' ? 'ay' : 'hafta'} kısmidir.
          </p>
          {breakdownView === 'monthly' && (
            <p className="text-xs text-amber-600 mb-3">
              ⚠️ Reklam harcaması & GA4 takibi <b>Mayıs 2025</b>'te başlıyor; öncesindeki aylarda ikas satışı görünür ama
              harcama/maliyet "—" gelir.
            </p>
          )}

          {breakdownView === 'monthly' ? (
            <BreakdownTable
              rows={monthly?.rows || []}
              periodKey="month"
              periodHeader="Ay"
              labelOf={(r) => fmtMonth(r.month)}
              open={openMonths}
              toggle={(pk, isOpen) => setOpenMonths((s) => ({ ...s, [pk]: !isOpen }))}
            />
          ) : (
            <BreakdownTable
              rows={weekly?.rows || []}
              periodKey="week"
              periodHeader="Hafta"
              labelOf={(r) => fmtWeek(r.week, r.weekEnd)}
              open={openWeeks}
              toggle={(pk, isOpen) => setOpenWeeks((s) => ({ ...s, [pk]: !isOpen }))}
            />
          )}
        </div>
      )}
    </div>
  );
}
